import {
  Checkbox,
  cn,
  MembershipBadge,
  ProductBadge,
  RectangleButton,
  Dialog,
  showToast,
} from '@yanolja-rnd/nol-design-system'
import { IcError } from '@yanolja-rnd/nol-icons'
import { useCallback, useMemo, useState } from 'react'
import { DomesticCartPropertyOption } from '~/api/converters/domestic/cart/ProductSection/Property/index.type'
import CancellationPolicy from '~/app/components-nds/common/cancellation-policy'
import ProductOutdated from '~/app/components-nds/common/product-outdated'
import ProductThumbnail from '~/app/components-nds/common/thumbnail'
import PrimaryProductBadges from '~/app/components-nds/domestic/common/primary-product-badges'
import DomesticPropertyRatePlanBenefits from '~/app/components-nds/domestic/common/rateplan-benefits/ index'
import { useDomesticCartQuery } from '~/app/react-query/domestic/cart'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import useModalHash from '~/app/components/common/useModalHash'
import DayUseTimelinesBottomSheet from '~/app/components-nds/domestic/cart/cart-product-section/items/property/dayuse-timelines-bottom-sheet'
import useDayuseTimelines from '~/app/components-nds/domestic/cart/hooks/use-dayuse-timelines'
import useDomesticCartOptionUpdate from '~/app/components-nds/domestic/cart/hooks/use-demestic-cart-option-update'
import { DomesticCartDayuseTimeLine } from '~/api/routes/domestic/cart/controller/type'
import OptionDeleteButton from '~/app/components-nds/domestic/cart/cart-product-section/items/common/option-delete-button'

const CheckInOutInfo = ({
  label,
  date,
  time,
}: {
  label: '체크인' | '체크아웃'
  date: string
  time: string
}) => {
  return (
    <div className={'text-center'}>
      <div className={'mb-6 typography-body-12-regular'}>{label}</div>
      <div className={'mb-2 typography-body-14-bold'}>{date}</div>
      <div className={'typography-body-14-bold'}>{time}</div>
    </div>
  )
}

type Props = DomesticCartPropertyOption & {
  goToPDP: () => void
}

/**
 * 장바구니 > 국내숙소 Items > Option
 */
const PropertyOption = (props: Props) => {
  const {
    optionId,
    primaryBadges,
    optionTitle,
    optionSubTitle,
    imageUrl,
    capacity,
    ratePlanBenefits,
    useTypeText,
    priceText,
    remainsText,
    isHotDeal,
    hasPropertyCoupon,
    isSaleAvailable,
    disabledText,
    checkInDate,
    checkOutDate,
    checkInDateYmd,
    checkOutDateYmd,
    checkInTime,
    checkOutTime,
    useTimeText,
    isShowDayUseTimeChange,
    isShowVipBenefitNotice,
    isShowVipBadge,
    vipDiscountPriceText,
    needUpdate,
    cancellationPolicy,
    goToPDP,
  } = props
  const { refetch } = useDomesticCartQuery()
  const { updateCartOption } = useDomesticCartOptionUpdate()

  /** 대실 시간 변경 바텀시트 표시 여부 */
  const [isDayUseTimeChangeBottomSheetOpen, setDayUseTimeChangeBottomSheetOpen] = useModalHash({
    id: `dayuse-time-change-bottom-sheet-${optionId}`,
  })
  /** 대실 시간 변경 확인 팝업 표시 여부 */
  const [isDayUseTimeChangeConfirmOpen, setDayUseTimeChangeConfirmOpen] = useModalHash({
    id: `dayuse-time-change-confirm-dialog-${optionId}`,
  })
  /** 확인 다이얼로그에서 사용할 선택된 타임라인 */
  const [beforeTimeLine, setBeforeTimeLine] = useState<DomesticCartDayuseTimeLine | null>(null)

  // 대실 시간 정보 조회 hook (로딩 시 실행)
  const { timeLines, selectedTimeLine, onSelectTimeLine, remainingHours, remainingMinutes } =
    useDayuseTimelines({
      optionId,
      checkInDateYmd,
      checkOutDateYmd,
      checkInTime,
      roomTypeId: props.roomTypeId,
      ratePlanId: props.ratePlanId,
      ratePlanVersion: props.ratePlanVersion,
      adults: props.adults,
      childAges: props.childAges,
      enabled: isShowDayUseTimeChange,
    })

  // 남은 시간 표시 조건
  const hasRemainingHours = remainingHours > 0
  const hasRemainingMinutes = remainingMinutes > 0
  const hasRemainingTime = hasRemainingHours || hasRemainingMinutes

  // 장바구니 상태 관리
  const { isSelected, selectOption, unselectOption } = useDomesticCartStore(
    ({ selectedOptionIds, selectOption, unselectOption }) => {
      return {
        isSelected: selectedOptionIds.has(optionId),
        selectOption,
        unselectOption,
      }
    }
  )

  // 체크박스 변경 핸들러
  const handleCheckboxChange = () => {
    if (isSelected) {
      unselectOption(optionId)
    } else {
      selectOption(optionId)
    }
  }

  const onApplyTimeLine = useCallback(
    (targetTimeLine: DomesticCartDayuseTimeLine) => {
      setDayUseTimeChangeConfirmOpen(false)
      updateCartOption(
        {
          optionId,
          domesticReservation: {
            checkInAt: targetTimeLine.checkInAt,
            checkOutAt: targetTimeLine.checkOutAt,
          },
        },
        {
          onSuccess: () => {
            showToast({
              children: '이용 시간을 변경했어요',
              clearOtherToast: true,
            })
          },
          onError: error => {
            if (error.response?.data?.code === 'CART_ADD_DOMESTIC_RESERVATION_001') {
              showToast({
                children: '이용 시간을 변경하지 못했어요',
                clearOtherToast: true,
              })
            }
          },
        }
      )
    },
    [optionId, updateCartOption, setDayUseTimeChangeConfirmOpen]
  )

  /**
   * 대실 시간 바텀 시터가 완전히 종료 후 실행되는 핸들러
   */
  const dayUseTimeChangeConfirm = useCallback(() => {
    if (selectedTimeLine && beforeTimeLine) {
      // 대실 마감 시간 이후로 선택된 경우 확인 다이얼로그 열기
      if (hasRemainingTime) {
        setDayUseTimeChangeConfirmOpen(true)
      } else {
        // 바로 업데이트 실행
        onApplyTimeLine(selectedTimeLine)
      }
    }
  }, [
    selectedTimeLine,
    beforeTimeLine,
    hasRemainingTime,
    setDayUseTimeChangeConfirmOpen,
    onApplyTimeLine,
  ])

  // 대실 시간 변경 바텀시트 선택 핸들러
  const dayUseTimeChange = useCallback(
    (targetTimeLine: DomesticCartDayuseTimeLine) => {
      if (targetTimeLine && selectedTimeLine) {
        // 이전 타임라인 저장
        setBeforeTimeLine(selectedTimeLine)
        // 선택된 타임라인 업데이트
        onSelectTimeLine(targetTimeLine)
      }
    },
    [onSelectTimeLine, selectedTimeLine]
  )

  /**
   * 대실 시간 변경 확인 팝업 취소 버튼 클릭 핸들러
   */
  const dayUseTimeChangeConfirmCancel = useCallback(() => {
    if (beforeTimeLine) {
      onSelectTimeLine(beforeTimeLine)
      setBeforeTimeLine(null)
    }
    setDayUseTimeChangeConfirmOpen(false)
  }, [beforeTimeLine, onSelectTimeLine, setDayUseTimeChangeConfirmOpen])

  /**
   * 대실 시간 변경 확인 팝업 승인 버튼 클릭 핸들러
   */
  const dayUseTimeChangeConfirmOk = useCallback(() => {
    if (selectedTimeLine) {
      onApplyTimeLine(selectedTimeLine)
      setBeforeTimeLine(null)
    }
  }, [selectedTimeLine, onApplyTimeLine, setBeforeTimeLine])

  const remainingTimeText = useMemo(() => {
    const parts: string[] = []
    if (remainingHours > 0) parts.push(`${remainingHours}시간`)
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}분`)
    return parts.length > 0 ? `${parts.join(' ')}만 이용` : ''
  }, [remainingHours, remainingMinutes])

  return (
    <div className={'grid w-full gap-16'}>
      <div>
        {/* 뱃지 (골드 클래스 뱃지, 특가 뱃지, 프로모션 뱃지) */}
        {(isShowVipBadge || !!primaryBadges?.length) && (
          <div className={'mb-8 flex gap-4'} id="primary-product-badges">
            {isShowVipBadge && (
              <MembershipBadge type="gold_special_price" size="medium">
                특가
              </MembershipBadge>
            )}
            {!!primaryBadges?.length && <PrimaryProductBadges badges={primaryBadges} />}
          </div>
        )}

        {/* 체크박스, 객실명, 삭제 버튼 */}
        <div className={'flex items-start gap-6'}>
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={!isSaleAvailable}
            onChange={handleCheckboxChange}
            aria-label={`${optionTitle} 선택`}
          />
          <div
            className={cn('flex-auto cursor-pointer typography-subtitle-16-bold', {
              'text-text-neutral-medium': !isSaleAvailable,
            })}
            onClick={goToPDP}
          >
            {optionTitle}
          </div>
          <OptionDeleteButton optionId={optionId} aria-label={`${optionTitle} 삭제`} />
        </div>
      </div>

      <div
        className={cn('grid w-full grid-cols-[auto_1fr] gap-12', {
          'text-text-neutral-medium': !isSaleAvailable,
        })}
      >
        {/* 썸네일 이미지 */}
        <ProductThumbnail
          image={imageUrl}
          isDisabled={!isSaleAvailable}
          className={'h-auto min-h-118 w-88 pc:w-108'}
        />
        {/* 숙소 정보*/}
        <div className={'flex flex-col justify-between gap-12'}>
          {/* 요금제, 인원, 옵션 정보 */}
          <div className={'col-2 grid w-full grid-rows-[auto_1fr] items-center gap-6'}>
            {optionSubTitle && (
              <div className={'typography-subtitle-16-bold'}>{optionSubTitle}</div>
            )}
            <div className={'typography-body-14-regular'}>{capacity}</div>
            {!!ratePlanBenefits?.length && (
              <DomesticPropertyRatePlanBenefits ratePlanBenefits={ratePlanBenefits} />
            )}
            <div className={'flex gap-4'}>
              {isHotDeal && (
                <ProductBadge type="filled_secondary" size="medium" className="w-fit">
                  핫딜
                </ProductBadge>
              )}
              {hasPropertyCoupon && (
                <ProductBadge type="filled_secondary" size="medium" className="w-fit">
                  선착순쿠폰
                </ProductBadge>
              )}
            </div>
          </div>
          {/* 가격 정보, 예약 불가 사유, 남은 객실 수 */}
          <div className={'grid grid-cols-[1fr_auto] items-start gap-4 text-right'}>
            <div className={'break-keep pt-2 typography-body-13-regular'}>{useTypeText}</div>
            <div
              className={cn(
                'break-all typography-subtitle-16-bold pc:typography-subtitle-18-bold',
                {
                  'line-through': !!disabledText,
                }
              )}
            >
              {priceText}
            </div>
            {remainsText && (
              <div
                className={'col-start-1 col-end-3 text-text-warning-main typography-body-12-bold'}
              >
                {remainsText}
              </div>
            )}
            {!isSaleAvailable && !!disabledText && (
              <div
                className={'col-start-1 col-end-3 text-text-neutral-main typography-body-12-bold'}
              >
                {disabledText}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 체크인/아웃 정보 */}
      <div
        className={cn(
          'grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 gap-y-16 rounded-12 bg-fill-neutral-weak p-16',
          {
            'text-text-neutral-medium': !isSaleAvailable,
          }
        )}
      >
        <CheckInOutInfo label="체크인" date={checkInDate} time={checkInTime} />
        <ProductBadge type="outlined_primary" size="large">
          {useTimeText}
        </ProductBadge>
        <CheckInOutInfo label="체크아웃" date={checkOutDate} time={checkOutTime} />
        {isShowDayUseTimeChange && (
          <RectangleButton
            theme="outlined_basic"
            weight="regular"
            size="small"
            className={'col-start-1 col-end-4 w-full'}
            onClick={() => setDayUseTimeChangeBottomSheetOpen(true)}
          >
            시간 변경
          </RectangleButton>
        )}
        {hasRemainingTime && (
          <div
            className={
              'col-start-1 col-end-4 flex w-full items-center justify-center gap-2 text-text-warning-main typography-body-12-regular-multiline'
            }
          >
            <IcError width={16} height={16} />
            <span>
              대실 마감으로 <strong>{remainingTimeText}</strong> 할 수 있어요.
            </span>
          </div>
        )}
      </div>

      {/* 상품 정보 변경 */}
      {needUpdate && <ProductOutdated />}

      {/* 취소규정 */}
      {!!cancellationPolicy &&
        (cancellationPolicy.type === 'policy-error' ? (
          <CancellationPolicy {...cancellationPolicy} onRefresh={() => refetch()} />
        ) : (
          <CancellationPolicy {...cancellationPolicy} />
        ))}

      {/* 멤버십 등급으로 인한 가격 인하 안내 */}
      {isShowVipBenefitNotice && (
        <div
          className={
            'grid gap-6 rounded-12 bg-fill-membership-weak p-16 text-center typography-body-14-regular'
          }
        >
          <div>
            <strong>
              <span className="text-text-membership-main">Gold Class</span>를 위한 특별한 혜택!
            </strong>
          </div>
          <div>
            <strong>{vipDiscountPriceText}</strong> 더 할인받았어요.
          </div>
        </div>
      )}
      {isShowDayUseTimeChange && (
        <DayUseTimelinesBottomSheet
          isOpen={isDayUseTimeChangeBottomSheetOpen}
          onClose={() => setDayUseTimeChangeBottomSheetOpen(false)}
          onConfirm={dayUseTimeChangeConfirm}
          timeLines={timeLines}
          initialTimeLine={selectedTimeLine}
          onChangeTimeLine={dayUseTimeChange}
        />
      )}
      <Dialog
        show={isDayUseTimeChangeConfirmOpen}
        title="이용 시간을 변경할까요?"
        description={
          hasRemainingTime ? `대실 마감으로 ${remainingTimeText} 할 수 있어요.` : undefined
        }
        negativeButton={{
          text: '아니요',
          onClick: dayUseTimeChangeConfirmCancel,
        }}
        positiveButton={{
          text: '변경하기',
          onClick: dayUseTimeChangeConfirmOk,
        }}
      />
    </div>
  )
}

export default PropertyOption
